import {
  find as lodashFpFind,
  flow,
  flatten,
  prop,
  defaultTo,
} from 'lodash/fp';
import { createSelector } from 'reselect';

import { getOpenedFiles } from 'store/utils/fileSystem';

// We need to apply the line bellow otherwise the argument index is not passed on find method
// https://github.com/lodash/lodash/issues/2387#issuecomment-242040423
const find = lodashFpFind.convert({ cap: false });

export const flowSelector = state => state.flow;

export const showSelector = createSelector(
  flowSelector,
  flow => flow.show,
);

export const getAllFiles = createSelector(
  flowSelector,
  flow => flow.all.data,
);

export const getFilesMapAsObject = createSelector(
  getAllFiles,
  data =>
    data.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item,
      }),
      {},
    ),
);

export const getSearchTerm = ({ flow }) => flow.all.searchTerm;

export const getIsLoading = ({ flow }) => flow.all.isLoading;

export const getIsUploading = ({ flow }) => flow.upload.isLoading;

export const getUploadingFiles = ({ flow }) => flow.upload.files;

export const getIsSaving = ({ flow }) => flow.show.isSaving;

export const getUploadingFilesWithError = createSelector(
  getUploadingFiles,
  files => files.filter(f => f.hasError === true),
);

export const getHasUploadingFilesError = createSelector(
  getUploadingFilesWithError,
  files => files.length !== 0,
);

export const getFilteredFiles = createSelector(
  getAllFiles,
  getSearchTerm,
  (files, searchTerm) =>
    files.filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase())),
);

export const getEditorFiles = createSelector(
  getAllFiles,
  all => {
    const openedFiles = getOpenedFiles(all);

    // Add full path as name to duplicated names that are opened .
    const updateFileName = (file, index) => {
      const constructUpdatedFilename = fileNameIsDuplicated =>
        fileNameIsDuplicated ? file.id : file.name;

      const constructUpdatedFile = updatedFilename => ({
        ...file,
        name: updatedFilename,
      });

      const compareIndices = (fileToCompare, fileIndexToCompare) =>
        index !== fileIndexToCompare && fileToCompare.name === file.name;

      return flow(
        find(compareIndices),
        Boolean,
        constructUpdatedFilename,
        constructUpdatedFile,
      )(openedFiles);
    };

    const updatedEditorFiles = openedFiles.map(updateFileName);

    return updatedEditorFiles;
  },
);

export const getEditorCurrentFile = createSelector(
  showSelector,
  getAllFiles,
  (show, all) =>
    all.find(currentFile => currentFile.id === show.presentedId) || {},
);

export const getEditorSearchTerm = ({ flow }) => flow.show.searchTerm;

export const getTheme = ({ flow }) => flow.show.theme;

export const getIsEditorLoading = ({ flow }) => flow.show.isLoading;

export const getExportAllIsLoading = ({ flow }) => flow.exportAll.isLoading;

export const getEditFolderError = ({ flow }) => flow.editFolder.error;

export const getEditFileError = ({ flow }) => flow.editFile.error;

export const getDeleteFolderError = ({ flow }) => flow.deleteFolder.error;

export const getDeleteFileError = ({ flow }) => flow.deleteFile.error;

export const getFilesWithConflicts = flow([
  prop('flow.upload.conflicts'),
  defaultTo({}),
  Object.values,
  flatten,
]);

export const getObsoleteOpenedFiles = createSelector(
  state => state,
  getAllFiles,
  (flowData, files) => {
    const filesWithConflicts = getFilesWithConflicts(flowData);
    const openedFiles = getOpenedFiles(files);

    const obsoleteFiles = openedFiles.reduce((acc, openedFile) => {
      const fileIsObsolete = Boolean(
        filesWithConflicts.find(conflictId => conflictId === openedFile.id),
      );
      const updatedAcc = fileIsObsolete ? [...acc, openedFile] : acc;

      return updatedAcc;
    }, []);

    return obsoleteFiles;
  },
);
